/**
 * The state of the ship, as a picture of the ship.
 *
 * A percentage on the console tells you how much hull is left and nothing about
 * *where* — and since damage started landing on the walls that were actually
 * hit, where is most of the information. A hull at eighty per cent with a bad
 * scrape down one flank and one at eighty per cent worn evenly are different
 * ships to be flying, and the second is in far less trouble than the first.
 *
 * So it is drawn rather than listed: the same top-down the editor uses, with
 * every wall coloured by what is left of it. You read it the way you read the
 * editor, which is the point — it is the same object.
 *
 * Toggled, like the hold. Most of a flight is not about the hull either.
 */

import { useState } from 'react';

import { LINE_HEALTH, type ShipGraph } from '@interspacial/shared';
import { HUD_BACKING, HUD_DIM, HUD_EDGE, HUD_ORANGE, HUD_TEXT } from './FlightHud';

/**
 * Whether anything is bad enough to be worth a light on the console.
 *
 * A missing wall counts even if everything still aboard is pristine — losing one
 * is the loudest thing that can happen to a hull, and the ship that is left has
 * nothing wrong with it by its own reckoning.
 */
export function hullUrgent(hull: ShipGraph, blueprint: ShipGraph): boolean {
  if (hull.lines.length < blueprint.lines.length) return true;
  return hull.lines.some((line) => line.health <= LINE_HEALTH * 0.35);
}

/** Amber worn, red nearly gone. Anything lost is not here to be coloured. */
function colourOf(fraction: number): string {
  if (fraction < 0.35) return '#ff5f6b';
  if (fraction < 0.8) return '#ffb347';
  return 'rgba(190,215,245,0.85)';
}

const VIEW = 132;

/**
 * The hull's state as a sentence, for the console to type.
 *
 * The picture is the useful part and a picture cannot be typed, so the text
 * carries the verdict — sound, dented, or missing walls — and `HullContents`
 * draws the rest underneath.
 */
export function hullSummary(hull: ShipGraph, blueprint: ShipGraph): string {
  const aboard = new Set(hull.lines.map((line) => line.id));
  const missing = blueprint.lines.filter((line) => !aboard.has(line.id)).length;
  const hurt = hull.lines.filter((line) => line.health < LINE_HEALTH).length;

  if (missing === 0 && hurt === 0) return 'Hull sound. No walls lost, none damaged.';

  const said: string[] = [];
  if (missing > 0) said.push(`${missing} ${missing === 1 ? 'wall' : 'walls'} lost`);
  if (hurt > 0) said.push(`${hurt} damaged`);

  return `${said.join(', ')}.`;
}

/**
 * The hull picture and its list, with no frame of its own.
 *
 * Split from the panel that wrapped it so the console can print it as a section
 * — the frame, the header and the verdict line belong to the console now.
 */
export function HullContents({ hull, blueprint }: { hull: ShipGraph; blueprint: ShipGraph }) {
  // From the plan, so a wall the ship has lost still has ends to draw between.
  const joints = new Map(blueprint.joints.map((joint) => [joint.id, joint]));
  for (const joint of hull.joints) joints.set(joint.id, joint);

  const aboard = new Set(hull.lines.map((line) => line.id));
  const missing = blueprint.lines.filter((line) => !aboard.has(line.id));

  /** Which row the pointer is over, so the picture can answer it. */
  const [hovered, setHovered] = useState<string | null>(null);

  /*
   * Fit the ship to the box, whatever shape it is.
   *
   * Measured from the joints rather than assumed, because a hull grows in the
   * editor and the panel has to keep showing all of it — and because a ship is
   * not centred on its own origin, so a fixed window would put half of some
   * designs off the edge.
   */
  // Framed on the plan rather than the wreck, so the picture does not zoom in
  // as the ship comes apart — where a wall *was* is most of the information.
  const xs = blueprint.joints.map((joint) => joint.x);
  const ys = blueprint.joints.map((joint) => joint.y);

  const minX = Math.min(...xs, 0);
  const maxX = Math.max(...xs, 0);
  const minY = Math.min(...ys, 0);
  const maxY = Math.max(...ys, 0);

  const span = Math.max(maxX - minX, maxY - minY, 1);
  const pad = 10;
  const scale = (VIEW - pad * 2) / span;

  // Screen y runs the other way to the ship's, so the nose points up.
  const px = (x: number) => pad + (x - minX) * scale + ((span - (maxX - minX)) * scale) / 2;
  const py = (y: number) => VIEW - pad - (y - minY) * scale - ((span - (maxY - minY)) * scale) / 2;

  const hurt = hull.lines.filter((line) => line.health < LINE_HEALTH).length;

  /*
   * The damaged walls, worst first, numbered.
   *
   * The number is the whole point of the list. Naming a wall by its length was
   * useless — a hull has several walls of much the same length and none of them
   * is anywhere in particular — so the row and the wall carry the same digit and
   * you read one off the other. Hovering a row lights its wall as well, which is
   * what you actually do when you want to know where something is.
   */
  const damaged = hull.lines
    .filter((line) => line.health < LINE_HEALTH)
    .sort((a, b) => a.health - b.health);

  const numberOf = new Map(damaged.map((line, index) => [line.id, index + 1]));

  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 5 }}>
        <svg
          width={VIEW}
          height={VIEW}
          viewBox={`0 0 ${VIEW} ${VIEW}`}
          style={{ flexShrink: 0, border: `1px solid ${HUD_EDGE}`, background: 'rgba(0,0,0,0.25)' }}
        >
          {/* What the ship should have and does not. Drawn first and faintly,
              so the shape of what is gone is there without competing with what
              is left. */}
          {missing.map((line) => {
            const a = joints.get(line.a);
            const b = joints.get(line.b);
            if (!a || !b) return null;

            return (
              <line
                key={`lost-${line.id}`}
                x1={px(a.x)}
                y1={py(a.y)}
                x2={px(b.x)}
                y2={py(b.y)}
                stroke="rgba(255,95,107,0.4)"
                strokeWidth={1}
                strokeDasharray="2 3"
              />
            );
          })}

          {hull.lines.map((line) => {
            const a = joints.get(line.a);
            const b = joints.get(line.b);
            if (!a || !b) return null;

            const left = Math.max(0, line.health) / LINE_HEALTH;
            const lit = hovered === line.id;

            return (
              <line
                key={line.id}
                x1={px(a.x)}
                y1={py(a.y)}
                x2={px(b.x)}
                y2={py(b.y)}
                stroke={lit ? '#ffffff' : colourOf(left)}
                strokeWidth={lit ? 3.5 : 2}
                strokeLinecap="round"
              />
            );
          })}

          {/* The numbers, on the walls they belong to. */}
          {damaged.map((line) => {
            const a = joints.get(line.a);
            const b = joints.get(line.b);
            if (!a || !b) return null;

            return (
              <text
                key={`n-${line.id}`}
                x={(px(a.x) + px(b.x)) / 2}
                y={(py(a.y) + py(b.y)) / 2}
                fill={hovered === line.id ? '#ffffff' : HUD_TEXT}
                fontSize={8}
                fontWeight={600}
                textAnchor="middle"
                dominantBaseline="central"
                // The wall is drawn under it; a dark halo keeps the digit
                // legible without a plate that would hide the wall itself.
                stroke="rgba(8,5,3,0.9)"
                strokeWidth={2.4}
                paintOrder="stroke"
                style={{ pointerEvents: 'none' }}
              >
                {numberOf.get(line.id)}
              </text>
            );
          })}
        </svg>

        {/* The numbers, for the walls that have something to say. A list of
            every wall at a hundred per cent is a list of nothing. */}
        <div style={{ flex: 1, minWidth: 0, maxHeight: VIEW, overflowY: 'auto', fontSize: 10.5 }}>
          {damaged.length === 0 && missing.length === 0 && (
            <div style={{ color: HUD_DIM }}>No damage.</div>
          )}

          {missing.length > 0 && (
            <div style={{ color: '#ff5f6b', lineHeight: 1.7 }}>
              {missing.length} wall{missing.length === 1 ? '' : 's'} gone
            </div>
          )}

          {damaged.map((line) => {
            const left = Math.max(0, line.health) / LINE_HEALTH;
            const lit = hovered === line.id;

            return (
              <div
                key={line.id}
                onPointerEnter={() => setHovered(line.id)}
                onPointerLeave={() => setHovered((current) => (current === line.id ? null : current))}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  color: lit ? '#ffffff' : colourOf(left),
                  background: lit ? 'rgba(255,138,61,0.16)' : undefined,
                  lineHeight: 1.7,
                  cursor: 'default',
                }}
              >
                <span
                  style={{
                    width: 13,
                    textAlign: 'center',
                    color: lit ? '#ffffff' : HUD_DIM,
                    border: `1px solid ${lit ? '#ffffff' : HUD_EDGE}`,
                    fontSize: 9,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {numberOf.get(line.id)}
                </span>
                <span style={{ flex: 1, fontVariantNumeric: 'tabular-nums' }}>
                  {`${Math.floor(left * 100)}%`}
                </span>
              </div>
            );
          })}
        </div>
    </div>
  );
}

const closeStyle: React.CSSProperties = {
  background: 'transparent',
  border: `1px solid ${HUD_EDGE}`,
  color: HUD_DIM,
  font: 'inherit',
  fontSize: 10,
  padding: '2px 6px',
  cursor: 'pointer',
};
